import React, { useCallback, useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import PersonOutlinedIcon from "@mui/icons-material/PersonOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import ShareOutlinedIcon from "@mui/icons-material/ShareOutlined";
import PersonAddAltOutlinedIcon from "@mui/icons-material/PersonAddAltOutlined";
import ContactPhoneOutlinedIcon from "@mui/icons-material/ContactPhoneOutlined";
import AddIcon from "@mui/icons-material/Add";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SearchIcon from "@mui/icons-material/Search";
import DriveFileRenameOutlineIcon from "@mui/icons-material/DriveFileRenameOutline";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import mobileCover from "images/Mobile-Cover.png";

import {
    fetchOrgAddressBookEntries,
    createOrgPhoneContact,
    updateOrgAddressBookEntry,
    deleteOrgAddressBookEntry,
    updateOrgAddressBook,
    uploadEntryAvatar,
} from "js/Api/address_book";
import ClassificationSelect, { ClassificationBadge } from "./ClassificationSelect";
import { validateName, validatePhoneNumber } from "js/helpers/common_functions";
import CopyContactsModal from "./CopyContactsModal";
import ShareAddressBookModal from "./ShareAddressBookModal";

const MAX_NAME_LENGTH = 80;

// ---------------------------------------------------------------------------
// Shared modal
// ---------------------------------------------------------------------------
function ModalOverlay({ onClose, children }) {
    return (
        <div
            style={{
                position: "fixed", inset: 0,
                background: "rgba(0,0,0,0.45)",
                zIndex: 1050,
                display: "flex", alignItems: "center", justifyContent: "center",
            }}
            onClick={onClose}
        >
            <div onClick={(e) => e.stopPropagation()}>{children}</div>
        </div>
    );
}

// Shared modal card shell
function ModalCard({ width = 480, children }) {
    return (
        <div style={{
            background: "#fff", borderRadius: 16,
            width, maxWidth: "92vw",
            boxShadow: "0 24px 64px rgba(0,0,0,0.18)",
            overflow: "hidden",
        }}>
            {children}
        </div>
    );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
function OrgAddressBookDetail({ book, allBooks, org, onBookRenamed }) {
    const isDefault = book.bookType === "system_default" || book.systemKey === "default";

    // ── Rename ───────────────────────────────────────────────────────────────
    const [editingName, setEditingName] = useState(false);
    const [nameInput, setNameInput] = useState(book.name);
    const [savingName, setSavingName] = useState(false);
    const renameInputRef = useRef(null);

    useEffect(() => {
        if (editingName) renameInputRef.current?.focus();
    }, [editingName]);

    const cancelRename = () => { setEditingName(false); setNameInput(book.name); };

    const handleRename = async () => {
        const trimmed = nameInput.trim();
        if (!trimmed || trimmed === book.name) { cancelRename(); return; }
        if (trimmed.length > MAX_NAME_LENGTH) { toast.error(`Max ${MAX_NAME_LENGTH} characters.`); return; }
        setSavingName(true);
        const res = await updateOrgAddressBook(org?.id, {
            id: book.id,
            name: trimmed,
            classification: book.classification || "safe",
        });
        setSavingName(false);
        if (res.success) { setEditingName(false); onBookRenamed(res.data); toast.success("Book renamed"); }
        else toast.error(res.error || "Failed to rename");
    };

    // ── Entries ──────────────────────────────────────────────────────────────
    const [entries, setEntries] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");

    const loadEntries = useCallback(async () => {
        setLoading(true);
        try {
            const res = await fetchOrgAddressBookEntries(book.id);
            if (res.success) setEntries(Array.isArray(res.data) ? res.data : []);
            else toast.error(res.error || "Failed to load contacts");
        } finally { setLoading(false); }
    }, [book.id]);

    useEffect(() => { loadEntries(); }, [loadEntries]);

    // ── Add ──────────────────────────────────────────────────────────────────
    const [showAddModal, setShowAddModal] = useState(false);

    // ── Edit ─────────────────────────────────────────────────────────────────
    const [editingEntry, setEditingEntry] = useState(null);

    // ── Delete ───────────────────────────────────────────────────────────────
    const [deletingEntry, setDeletingEntry] = useState(null);
    const [deleting, setDeleting] = useState(false);

    const handleConfirmDelete = async () => {
        if (!deletingEntry) return;
        setDeleting(true);
        const res = await deleteOrgAddressBookEntry(deletingEntry.id);
        setDeleting(false);
        if (res.success) {
            toast.success("Contact deleted");
            setEntries((prev) => prev.filter((e) => e.id !== deletingEntry.id));
            setDeletingEntry(null);
        } else toast.error(res.error || "Failed to delete contact");
    };

    // ── Copy ─────────────────────────────────────────────────────────────────
    const [showCopyModal, setShowCopyModal] = useState(false);
    // "pool" = copy from the org contact list (default book);
    // "books" = copy from another (custom) address book.
    const [copyMode, setCopyMode] = useState(null);
    const [showAddMenu, setShowAddMenu] = useState(false);
    const [showShareModal, setShowShareModal] = useState(false);
    const addMenuRef = useRef(null);

    const isDefaultB = (b) => b?.bookType === "system_default" || b?.systemKey === "default";
    const poolBook = allBooks.find((b) => isDefaultB(b) && b.id !== book.id) || null;
    const otherBooks = allBooks.filter((b) => b.id !== book.id && !isDefaultB(b));
    const copySourceBooks = copyMode === "pool"
        ? (poolBook ? [poolBook] : [])
        : otherBooks;

    useEffect(() => {
        const handler = (e) => {
            if (addMenuRef.current && !addMenuRef.current.contains(e.target)) {
                setShowAddMenu(false);
            }
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    const openCopy = (mode) => {
        setCopyMode(mode);
        setShowCopyModal(true);
        setShowAddMenu(false);
    };

    // ── Filter ───────────────────────────────────────────────────────────────
    const filteredEntries = entries
        .filter((e) => {
            const q = searchQuery.trim().toLowerCase();
            if (!q) return true;
            return (
                (e.displayName || "").toLowerCase().includes(q) ||
                (e.phoneNumberRaw || "").toLowerCase().includes(q)
            );
        })
        // Sort alphabetically (A–Z) by display name.
        .sort((a, b) =>
            (a.displayName || "").localeCompare(b.displayName || "", undefined, { sensitivity: "base" })
        );

    // ── Render ───────────────────────────────────────────────────────────────
    return (
        <div style={{ padding: "24px 28px" }}>

            {/* ── Rename bar (custom books only) ── */}
            {!isDefault && (
                <div style={{
                    display: "flex", alignItems: "center", gap: 10,
                    marginBottom: 24,
                    padding: "12px 16px",
                    background: editingName ? "#f0f7ff" : "#fafafa",
                    border: `1px solid ${editingName ? "#90caf9" : "#eee"}`,
                    borderRadius: 10,
                    transition: "all 0.15s",
                }}>
                    <DriveFileRenameOutlineIcon sx={{ fontSize: "1.1rem", color: "#888", flexShrink: 0 }} />
                    {editingName ? (
                        <>
                            <input
                                ref={renameInputRef}
                                type="text"
                                value={nameInput}
                                maxLength={MAX_NAME_LENGTH}
                                onChange={(e) => setNameInput(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") handleRename();
                                    if (e.key === "Escape") cancelRename();
                                }}
                                disabled={savingName}
                                style={{
                                    flex: 1, border: "none", background: "transparent",
                                    fontSize: 14, outline: "none", color: "#1a1a2e",
                                }}
                            />
                            <button
                                onClick={handleRename}
                                disabled={savingName || !nameInput.trim()}
                                title="Save"
                                style={{
                                    background: "#1976d2", border: "none", borderRadius: 6,
                                    color: "#fff", padding: "4px 8px", cursor: "pointer",
                                    display: "flex", alignItems: "center",
                                }}
                            >
                                <CheckIcon sx={{ fontSize: "1rem" }} />
                            </button>
                            <button
                                onClick={cancelRename}
                                disabled={savingName}
                                title="Cancel"
                                style={{
                                    background: "#f3f4f6", border: "none", borderRadius: 6,
                                    color: "#666", padding: "4px 8px", cursor: "pointer",
                                    display: "flex", alignItems: "center",
                                }}
                            >
                                <CloseIcon sx={{ fontSize: "1rem" }} />
                            </button>
                        </>
                    ) : (
                        <>
                            <span style={{ flex: 1, fontSize: 14, color: "#666" }}>
                                Book name: <strong style={{ color: "#333" }}>{book.name}</strong>
                            </span>
                            <button
                                onClick={() => setEditingName(true)}
                                style={{
                                    background: "none", border: "1px solid #ddd", borderRadius: 6,
                                    color: "#555", padding: "4px 12px", cursor: "pointer",
                                    fontSize: 13, display: "flex", alignItems: "center", gap: 4,
                                    transition: "all 0.12s",
                                }}
                                onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#1976d2"; e.currentTarget.style.color = "#1976d2"; }}
                                onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#ddd"; e.currentTarget.style.color = "#555"; }}
                            >
                                <EditOutlinedIcon sx={{ fontSize: "0.95rem" }} />
                                Rename
                            </button>
                        </>
                    )}
                </div>
            )}

            {/* ── Toolbar ── */}
            <div style={{
                display: "flex", alignItems: "center", gap: 10,
                marginBottom: 20, flexWrap: "wrap",
            }}>
                {isDefault ? (
                    /* Pool / contact-list view — manual add only. */
                    <button
                        type="button"
                        className="btn btn-primary"
                        onClick={() => setShowAddModal(true)}
                        style={{ display: "inline-flex", alignItems: "center", gap: 6, whiteSpace: "nowrap" }}
                    >
                        <AddIcon sx={{ fontSize: "1.1rem" }} />
                        Add Contact
                    </button>
                ) : (
                    /* Custom book — dropdown with manual + copy actions. */
                    <div ref={addMenuRef} style={{ position: "relative" }}>
                        <button
                            type="button"
                            className="btn btn-primary"
                            onClick={() => setShowAddMenu((o) => !o)}
                            style={{ display: "inline-flex", alignItems: "center", gap: 6, whiteSpace: "nowrap" }}
                        >
                            <AddIcon sx={{ fontSize: "1.1rem" }} />
                            Add Contact
                            <ExpandMoreIcon sx={{ fontSize: "1.1rem", transform: showAddMenu ? "rotate(180deg)" : "none", transition: "transform 0.15s" }} />
                        </button>

                        {showAddMenu && (
                            <div style={{
                                position: "absolute", top: "calc(100% + 6px)", left: 0,
                                background: "#fff", border: "1px solid #e0e0e0", borderRadius: 10,
                                boxShadow: "0 8px 28px rgba(0,0,0,0.14)", zIndex: 200,
                                minWidth: 240, overflow: "hidden",
                            }}>
                                <MenuItem
                                    icon={<PersonAddAltOutlinedIcon sx={{ fontSize: "1.1rem", color: "#1976d2" }} />}
                                    label="Add contact manually"
                                    onClick={() => { setShowAddModal(true); setShowAddMenu(false); }}
                                />
                                {poolBook && (
                                    <MenuItem
                                        icon={<ContactPhoneOutlinedIcon sx={{ fontSize: "1.1rem", color: "#1976d2" }} />}
                                        label="Copy from contact list"
                                        onClick={() => openCopy("pool")}
                                    />
                                )}
                                {otherBooks.length > 0 && (
                                    <MenuItem
                                        icon={<ContentCopyOutlinedIcon sx={{ fontSize: "1rem", color: "#1976d2" }} />}
                                        label="Copy from another address book"
                                        onClick={() => openCopy("books")}
                                    />
                                )}
                            </div>
                        )}
                    </div>
                )}

                {/* Share — custom books only (not the contact pool) */}
                {!isDefault && (
                    <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => setShowShareModal(true)}
                        style={{ display: "inline-flex", alignItems: "center", gap: 6, whiteSpace: "nowrap" }}
                    >
                        <ShareOutlinedIcon sx={{ fontSize: "1.05rem" }} />
                        Share
                    </button>
                )}

                {/* Search — pushed right */}
                <div style={{ position: "relative", marginLeft: "auto", minWidth: 200, maxWidth: 300, flex: "0 1 260px" }}>
                    <SearchIcon style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "#bbb", fontSize: 17, pointerEvents: "none" }} />
                    <input
                        type="text"
                        placeholder="Search contacts…"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        style={{
                            width: "100%", paddingLeft: 32, paddingRight: 12,
                            paddingTop: 8, paddingBottom: 8,
                            border: "1px solid #e0e0e0", borderRadius: 8,
                            fontSize: 14, outline: "none", background: "#fafafa",
                            boxSizing: "border-box", transition: "border-color 0.15s",
                        }}
                        onFocus={(e) => e.target.style.borderColor = "#1976d2"}
                        onBlur={(e) => e.target.style.borderColor = "#e0e0e0"}
                    />
                </div>
            </div>

            {/* ── Contact list ── */}
            {loading ? (
                <div style={{ textAlign: "center", padding: "60px 0", color: "#aaa" }}>
                    <i className="fa fa-spinner fa-spin" style={{ fontSize: 26, color: "#1976d2" }} />
                    <p style={{ marginTop: 12 }}>Loading contacts…</p>
                </div>
            ) : entries.length === 0 ? (
                <div style={{
                    textAlign: "center", padding: "60px 20px",
                    border: "2px dashed #eee", borderRadius: 12, color: "#aaa",
                }}>
                    <PersonOutlinedIcon sx={{ fontSize: "3rem", color: "#e0e0e0", display: "block", margin: "0 auto 12px" }} />
                    <p style={{ fontSize: 15, marginBottom: 12 }}>No contacts yet</p>
                    <button
                        type="button"
                        className="btn btn-primary"
                        onClick={() => setShowAddModal(true)}
                    >
                        + Add your first contact
                    </button>
                </div>
            ) : filteredEntries.length === 0 ? (
                <div style={{ textAlign: "center", padding: "40px 0", color: "#aaa" }}>
                    No contacts match "{searchQuery.trim()}"
                </div>
            ) : (
                <>
                    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                        {filteredEntries.map((entry) => (
                            <ContactRow
                                key={entry.id}
                                entry={entry}
                                onEdit={() => setEditingEntry(entry)}
                                onDelete={() => setDeletingEntry(entry)}
                            />
                        ))}
                    </div>
                    <p style={{ fontSize: 12, color: "#bbb", marginTop: 12, textAlign: "right" }}>
                        {filteredEntries.length} of {entries.length} contact{entries.length !== 1 ? "s" : ""}
                    </p>
                </>
            )}

            {/* ── Add modal ── */}
            {showAddModal && (
                <ContactFormModal
                    title="Add Contact"
                    confirmLabel="Add Contact"
                    defaultClassification={book.classification || "safe"}
                    onClose={() => setShowAddModal(false)}
                    onSave={async ({ displayName, phoneNumberRaw, classification, file }) => {
                        const res = await createOrgPhoneContact(book.id, {
                            displayName, phoneNumberRaw, classification, file,
                        });
                        if (res.success) {
                            toast.success("Contact added");
                            setEntries((prev) => [res.data, ...prev]);
                            setShowAddModal(false);
                        } else {
                            toast.error(res.error || "Failed to add contact");
                        }
                    }}
                />
            )}

            {/* ── Edit modal ── */}
            {editingEntry && (
                <ContactFormModal
                    title="Edit Contact"
                    confirmLabel="Save Changes"
                    initialName={editingEntry.displayName}
                    initialPhone={editingEntry.phoneNumberRaw}
                    initialClassification={editingEntry.classification || "safe"}
                    initialAvatarUrl={editingEntry.avatarUrl}
                    onClose={() => setEditingEntry(null)}
                    onSave={async ({ displayName, phoneNumberRaw, classification, file }) => {
                        const body = {};
                        if (displayName !== editingEntry.displayName) body.displayName = displayName;
                        if (phoneNumberRaw !== editingEntry.phoneNumberRaw) body.phoneNumberRaw = phoneNumberRaw;
                        if (classification !== (editingEntry.classification || "safe")) body.classification = classification;

                        let updated = editingEntry;
                        if (Object.keys(body).length > 0) {
                            const res = await updateOrgAddressBookEntry(editingEntry.id, body);
                            if (!res.success) { toast.error(res.error || "Failed to update contact"); return; }
                            updated = res.data;
                        }

                        // A newly picked photo is uploaded via the dedicated avatar endpoint.
                        if (file) {
                            const av = await uploadEntryAvatar(editingEntry.id, file);
                            if (!av.success) { toast.error(av.error || "Failed to upload photo"); return; }
                            updated = av.data || updated;
                        }

                        toast.success("Contact updated");
                        setEntries((prev) => prev.map((e) => e.id === editingEntry.id ? { ...e, ...updated } : e));
                        setEditingEntry(null);
                    }}
                />
            )}

            {/* ── Delete confirmation ── */}
            {deletingEntry && (
                <ModalOverlay onClose={() => !deleting && setDeletingEntry(null)}>
                    <ModalCard width={420}>
                        <div style={{ padding: "28px 28px 0", display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                            <div>
                                <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: "#1a1a2e" }}>Delete Contact</h3>
                                <p style={{ margin: "6px 0 0", fontSize: 13, color: "#888" }}>This action cannot be undone.</p>
                            </div>
                            <button onClick={() => setDeletingEntry(null)} disabled={deleting}
                                style={{ background: "none", border: "none", fontSize: 22, color: "#aaa", cursor: "pointer", lineHeight: 1, padding: 0 }}>×</button>
                        </div>
                        <div style={{ padding: "16px 28px" }}>
                            <p style={{ fontSize: 14, color: "#444", marginBottom: 12 }}>
                                Are you sure you want to remove <strong>{deletingEntry.displayName}</strong>?
                            </p>
                            <div style={{
                                display: "flex", alignItems: "center", gap: 12,
                                padding: "12px 14px", background: "#fafafa",
                                border: "1px solid #eee", borderRadius: 10,
                            }}>
                                <img
                                    src={deletingEntry.avatarUrl || mobileCover}
                                    alt=""
                                    style={{ width: 36, height: 36, borderRadius: "50%", objectFit: "cover" }}
                                    onError={(e) => { e.currentTarget.src = mobileCover; }}
                                />
                                <div>
                                    <div style={{ fontWeight: 600, fontSize: 14, color: "#1a1a2e" }}>{deletingEntry.displayName}</div>
                                    <div style={{ fontSize: 12, color: "#888" }}>{deletingEntry.phoneNumberRaw}</div>
                                </div>
                            </div>
                        </div>
                        <div style={{ padding: "0 28px 24px", display: "flex", justifyContent: "flex-end", gap: 10 }}>
                            <button type="button" className="btn btn-secondary" onClick={() => setDeletingEntry(null)} disabled={deleting}>
                                Cancel
                            </button>
                            <button type="button" className="btn btn-danger" onClick={handleConfirmDelete} disabled={deleting}>
                                {deleting ? "Deleting…" : "Delete"}
                            </button>
                        </div>
                    </ModalCard>
                </ModalOverlay>
            )}

            {/* ── Copy modal ── */}
            {showCopyModal && (
                <CopyContactsModal
                    targetBook={book}
                    sourceBooks={copySourceBooks}
                    onClose={() => { setShowCopyModal(false); setCopyMode(null); }}
                    onDone={loadEntries}
                />
            )}

            {/* ── Share modal ── */}
            {showShareModal && (
                <ShareAddressBookModal
                    book={book}
                    org={org}
                    onClose={() => setShowShareModal(false)}
                />
            )}
        </div>
    );
}

// ---------------------------------------------------------------------------
// ContactRow
// ---------------------------------------------------------------------------
function ContactRow({ entry, onEdit, onDelete }) {
    const avatarSrc = entry.avatarUrl
        ? `${entry.avatarUrl}${entry.avatarUpdatedAt ? `?v=${encodeURIComponent(entry.avatarUpdatedAt)}` : ""}`
        : null;

    // Generate initials avatar colour from name
    const hue = (entry.displayName || "X").charCodeAt(0) * 13 % 360;
    const avatarBg = `hsl(${hue},55%,55%)`;
    const initials = (entry.displayName || "?")
        .split(" ").slice(0, 2).map((w) => w[0]).join("").toUpperCase();

    return (
        <div
            style={{
                display: "flex", alignItems: "center", gap: 14,
                padding: "12px 16px",
                background: "#fff",
                border: "1px solid #f0f0f0",
                borderRadius: 10,
                cursor: "default",
            }}
        >
            {/* Avatar */}
            {avatarSrc ? (
                <img
                    src={avatarSrc}
                    alt={entry.displayName}
                    style={{ width: 40, height: 40, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }}
                    onError={(e) => { e.currentTarget.style.display = "none"; }}
                />
            ) : (
                <div style={{
                    width: 40, height: 40, borderRadius: "50%",
                    background: avatarBg, color: "#fff",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 15, fontWeight: 700, flexShrink: 0,
                }}>
                    {initials}
                </div>
            )}

            {/* Name + phone */}
            <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ fontWeight: 600, fontSize: 14, color: "#1a1a2e", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {entry.displayName || "—"}
                    </span>
                    <ClassificationBadge value={entry.classification || "safe"} />

                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 4, marginTop: 2 }}>
                    <PhoneOutlinedIcon sx={{ fontSize: "0.75rem", color: "#1976d2" }} />
                    <span style={{ fontSize: 13, color: "#666", fontFamily: "monospace" }}>
                        {entry.phoneNumberRaw || "—"}
                    </span>
                </div>
            </div>

            {/* Actions — always visible */}
            <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                <button
                    onClick={onEdit}
                    title="Edit"
                    style={{
                        background: "#e8f0fe", border: "none", borderRadius: 7,
                        color: "#1976d2", padding: "7px 12px",
                        cursor: "pointer", display: "flex", alignItems: "center",
                        transition: "background 0.12s",
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = "#c5d8f5"}
                    onMouseLeave={(e) => e.currentTarget.style.background = "#e8f0fe"}
                >
                    <EditOutlinedIcon sx={{ fontSize: "1.05rem" }} />
                </button>
                <button
                    onClick={onDelete}
                    title="Delete"
                    style={{
                        background: "#fef2f2", border: "none", borderRadius: 7,
                        color: "#dc2626", padding: "7px 12px",
                        cursor: "pointer", display: "flex", alignItems: "center",
                        transition: "background 0.12s",
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = "#fee2e2"}
                    onMouseLeave={(e) => e.currentTarget.style.background = "#fef2f2"}
                >
                    <DeleteOutlineIcon sx={{ fontSize: "1.05rem" }} />
                </button>
            </div>
        </div>
    );
}

// ---------------------------------------------------------------------------
// ContactFormModal — shared for Add and Edit
// ---------------------------------------------------------------------------
function ContactFormModal({
    title,
    confirmLabel,
    initialName = "",
    initialPhone = "",
    initialClassification,
    initialAvatarUrl = null,
    defaultClassification = "safe",
    onClose,
    onSave,
}) {
    const [displayName, setDisplayName] = useState(initialName);
    const [phoneNumberRaw, setPhoneNumberRaw] = useState(initialPhone);
    const [classification, setClassification] = useState(
        initialClassification || defaultClassification || "safe"
    );
    const [file, setFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(initialAvatarUrl || null);
    const [saving, setSaving] = useState(false);
    const nameRef = useRef(null);
    const fileRef = useRef(null);

    useEffect(() => { nameRef.current?.focus(); }, []);
    useEffect(() => {
        // Revoke object URLs created for local previews to avoid leaks.
        return () => { if (file && previewUrl) URL.revokeObjectURL(previewUrl); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [previewUrl]);

    const handlePickFile = (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        if (!f.type.startsWith("image/")) { toast.error("Please choose an image file."); return; }
        if (f.size > 5 * 1024 * 1024) { toast.error("Image must be 5 MB or smaller."); return; }
        setFile(f);
        setPreviewUrl(URL.createObjectURL(f));
    };

    const handleSave = async () => {
        const nameResult = validateName(displayName);
        if (!nameResult.valid) { toast.error(nameResult.error); return; }
        const phoneResult = validatePhoneNumber(phoneNumberRaw);
        if (!phoneResult.valid) { toast.error(phoneResult.error); return; }
        setSaving(true);
        await onSave({
            displayName: nameResult.value,
            phoneNumberRaw: phoneResult.value,
            classification,
            file,
        });
        setSaving(false);
    };

    const fieldStyle = {
        width: "100%", padding: "10px 14px",
        border: "1.5px solid #e0e0e0", borderRadius: 8,
        fontSize: 14, outline: "none", boxSizing: "border-box",
        transition: "border-color 0.15s", background: "#fff",
    };

    return (
        <ModalOverlay onClose={() => !saving && onClose()}>
            <ModalCard width={460}>
                {/* Header */}
                <div style={{ padding: "24px 28px 0", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div>
                        <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: "#1a1a2e" }}>{title}</h3>
                    </div>
                    <button onClick={() => !saving && onClose()}
                        style={{ background: "none", border: "none", fontSize: 22, color: "#aaa", cursor: "pointer", lineHeight: 1, padding: 0 }}>×</button>
                </div>

                {/* Body */}
                <div style={{ padding: "20px 28px" }}>
                    {/* Photo picker */}
                    <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 18 }}>
                        <div style={{
                            width: 64, height: 64, borderRadius: "50%",
                            background: "#f0f3f8", border: "1px solid #e0e0e0",
                            display: "flex", alignItems: "center", justifyContent: "center",
                            overflow: "hidden", flexShrink: 0,
                        }}>
                            {previewUrl ? (
                                <img src={previewUrl} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                            ) : (
                                <PersonOutlinedIcon sx={{ fontSize: "2rem", color: "#bbb" }} />
                            )}
                        </div>
                        <div>
                            <input
                                ref={fileRef}
                                type="file"
                                accept="image/*"
                                onChange={handlePickFile}
                                disabled={saving}
                                style={{ display: "none" }}
                            />
                            <button
                                type="button"
                                className="btn btn-secondary"
                                onClick={() => fileRef.current?.click()}
                                disabled={saving}
                                style={{ fontSize: 13 }}
                            >
                                {previewUrl ? "Change photo" : "Add photo"}
                            </button>
                            {previewUrl && (
                                <button
                                    type="button"
                                    onClick={() => { setFile(null); setPreviewUrl(null); if (fileRef.current) fileRef.current.value = ""; }}
                                    disabled={saving}
                                    style={{
                                        marginLeft: 8, background: "none", border: "none",
                                        color: "#dc2626", fontSize: 13, cursor: "pointer",
                                    }}
                                >
                                    Remove
                                </button>
                            )}
                            <p style={{ margin: "6px 0 0", fontSize: 12, color: "#aaa" }}>
                                Optional. JPG/PNG, up to 5 MB.
                            </p>
                        </div>
                    </div>

                    {/* Name field */}
                    <div style={{ marginBottom: 16 }}>
                        <label style={{ fontSize: 13, fontWeight: 600, color: "#444", display: "block", marginBottom: 6 }}>
                            Full name <span style={{ color: "#e53e3e" }}>*</span>
                        </label>
                        <input
                            ref={nameRef}
                            type="text"
                            value={displayName}
                            onChange={(e) => setDisplayName(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && handleSave()}
                            disabled={saving}
                            placeholder="e.g. Jane Smith"
                            style={fieldStyle}
                            onFocus={(e) => e.target.style.borderColor = "#1976d2"}
                            onBlur={(e) => e.target.style.borderColor = "#e0e0e0"}
                        />
                    </div>

                    {/* Phone field */}
                    <div>
                        <label style={{ fontSize: 13, fontWeight: 600, color: "#444", display: "block", marginBottom: 6 }}>
                            Phone number <span style={{ color: "#e53e3e" }}>*</span>
                        </label>
                        <div style={{ position: "relative" }}>
                            <PhoneOutlinedIcon style={{
                                position: "absolute", left: 12, top: "50%",
                                transform: "translateY(-50%)", color: "#bbb", fontSize: 17,
                            }} />
                            <input
                                type="text"
                                value={phoneNumberRaw}
                                onChange={(e) => setPhoneNumberRaw(e.target.value)}
                                onKeyDown={(e) => e.key === "Enter" && handleSave()}
                                disabled={saving}
                                placeholder="+61 400 123 456"
                                style={{ ...fieldStyle, paddingLeft: 38 }}
                                onFocus={(e) => e.target.style.borderColor = "#1976d2"}
                                onBlur={(e) => e.target.style.borderColor = "#e0e0e0"}
                            />
                        </div>
                        <p style={{ margin: "5px 0 0", fontSize: 12, color: "#aaa" }}>
                            Include country code, e.g. +61 400 123 456
                        </p>
                    </div>

                    {/* Classification */}
                    <div style={{ marginTop: 16 }}>
                        <ClassificationSelect
                            value={classification}
                            onChange={setClassification}
                            disabled={saving}
                        />
                    </div>
                </div>

                {/* Footer */}
                <div style={{ padding: "0 28px 24px", display: "flex", justifyContent: "flex-end", gap: 10 }}>
                    <button type="button" className="btn btn-secondary" onClick={() => !saving && onClose()} disabled={saving}>
                        Cancel
                    </button>
                    <button type="button" className="btn btn-primary" onClick={handleSave} disabled={saving || !displayName.trim() || !phoneNumberRaw.trim()}>
                        {saving ? "Saving…" : confirmLabel}
                    </button>
                </div>
            </ModalCard>
        </ModalOverlay>
    );
}

// ---------------------------------------------------------------------------
// MenuItem — single row in the Add Contact dropdown
// ---------------------------------------------------------------------------
function MenuItem({ icon, label, onClick }) {
    const [hover, setHover] = useState(false);
    return (
        <button
            type="button"
            onClick={onClick}
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
            style={{
                width: "100%", display: "flex", alignItems: "center", gap: 10,
                padding: "11px 16px", border: "none",
                background: hover ? "#f5f8ff" : "#fff",
                color: "#1a1a2e", fontSize: 14, cursor: "pointer",
                textAlign: "left", transition: "background 0.1s",
            }}
        >
            {icon}
            <span>{label}</span>
        </button>
    );
}

export default OrgAddressBookDetail;
